import React, { useState } from "react";
import { useSelector } from "react-redux";
import "./style.scss";
import SellerFooterInfo from "./SellerFooterInfo";
import { useTranslation } from "react-i18next";
import EALink from "../../app/components/shared/Link/EALink";
import EAPageMessage from "../../app/components/shared/EAPageMessage";
import { State } from "../../app/store";
import useShowNavBar from "../NavBar/hooks/useShowNavBar";
import LinkedFacilitiesSwitcher from "../NavBar/shared/LinkedFacilitiesSwitcher";
import CookieSettingsModal from "../CookieBanner/CookieSettingsModal";
import { useCookieConsent } from "../../utils/cookieConsent/useCookieConsent";
import useFacility from "../../utils/hooks/useFacility";
import {
  EAKTYWNI_TERMS_URL,
  isTTPayEnabled,
  TTPAY_FOOTER_ANCHOR_ID,
  TTPAY_I18N_VALUES,
  TTPAY_PRIVACY_URL,
  TTPAY_TERMS_URL,
} from "../../app/utils/termsLinks";

const buildStamp = process.env.REACT_APP_BUILD_STAMP;

const formatBuildStamp = (stamp: string) => {
  if (!/^\d{6}\.\d{4}$/.test(stamp)) {
    return stamp;
  }
  const [date, time] = stamp.split(".");
  return `${date.slice(4, 6)}.${date.slice(2, 4)}.20${date.slice(0, 2)}, ${time.slice(0, 2)}:${time.slice(2, 4)}`;
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const showNavBar = useShowNavBar();
  const messageFooter = useSelector((state: State) => state.config.configuration?.message_footer ?? "");
  const configuration = useSelector((state: State) => state.config.configuration);
  const facilityId = useFacility();
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false);

  const { consentValue, hasAnyOptionalTag, resolvedTags, rejectOptional, updateConsent } = useCookieConsent(
    facilityId,
    configuration,
  );

  const privacyPolicyUrl = configuration.regulaminList?.find((r) => r.active)?.file ?? "";
  const showCookieSettings = !!configuration.cookieBarActive && hasAnyOptionalTag;
  const showTTPayInfo = isTTPayEnabled(configuration);

  if (!showNavBar) {
    return <></>;
  }

  return (
    <>
      <EAPageMessage message={messageFooter} />
      <div className={"footer"}>
        <div className="footer__facility-switcher">
          <LinkedFacilitiesSwitcher />
        </div>
        <div className="footer__content flex-between flex-wrap">
          <span>
            <SellerFooterInfo />
          </span>
          <div className="flex-center-center">
            <span>
              {t("dostawca_systemu_do_sprzedazy_biletow_karnetow_i_uslug_online_jest_eaktywni")}
              <EALink to="https://www.eaktywni.pl" title="www.eAktywni.pl" />
            </span>
          </div>
        </div>
        {showTTPayInfo && (
          <div className="footer__ttpay" id={TTPAY_FOOTER_ANCHOR_ID}>
            <p>{t("ttpay_footer_payment_info", TTPAY_I18N_VALUES)}</p>
            <div className="footer__ttpay-links">
              <EALink to={EAKTYWNI_TERMS_URL} title={t("regulamin_eaktywni")} />
              <EALink to={TTPAY_TERMS_URL} title={t("ttpay_terms")} />
              <EALink to={TTPAY_PRIVACY_URL} title={t("ttpay_privacy")} />
            </div>
          </div>
        )}
        {showCookieSettings && (
          <div className="footer__cookie-settings">
            <button
              type="button"
              className="footer__cookie-settings-btn"
              onClick={() => setIsCookieSettingsOpen(true)}
            >
              {t("cookie_settings_manage")}
            </button>
          </div>
        )}
        {buildStamp ? (
          <div className="footer__build" title={formatBuildStamp(buildStamp)}>
            Build {buildStamp}
          </div>
        ) : null}
      </div>

      <CookieSettingsModal
        isOpen={isCookieSettingsOpen}
        onClose={() => setIsCookieSettingsOpen(false)}
        onUpdateConsent={(optional) => {
          updateConsent(optional);
          setIsCookieSettingsOpen(false);
        }}
        onRejectOptional={() => {
          rejectOptional();
          setIsCookieSettingsOpen(false);
        }}
        resolvedTags={resolvedTags}
        privacyPolicyUrl={privacyPolicyUrl}
        facilityName={configuration.facilityName}
        currentConsent={consentValue}
      />
    </>
  );
};

export default Footer;
